import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LanguageIcon from '@mui/icons-material/Language';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LeadCommercialChart from './LeadCommercialChart';
import LeadCredcoTrendChart from './LeadCredcoTrendChart';
import { getLeadIntelligence, LeadIntelligence } from './leadsApi';
import { formatInr, formatPercent, turnoverSourceLabel } from './leadFormatters';

const bucketColors: Record<string, 'warning' | 'default' | 'success'> = {
  Gold: 'warning',
  Silver: 'default',
  Bronze: 'success',
};

const ScoreBar = ({ label, value, max = 100 }: { label: string; value: number; max?: number }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, (value / max) * 100)}
      sx={{ height: 8, borderRadius: 1 }}
    />
  </Box>
);

const PresenceChip = ({
  label,
  active,
  href,
}: {
  label: string;
  active?: boolean;
  href?: string | null;
}) => (
  <Chip
    size="small"
    icon={active ? <CheckCircleIcon /> : <CancelIcon />}
    label={label}
    color={active ? 'success' : 'default'}
    variant={active ? 'filled' : 'outlined'}
    component={href ? 'a' : 'div'}
    href={href || undefined}
    target={href ? '_blank' : undefined}
    rel={href ? 'noreferrer' : undefined}
    clickable={Boolean(href)}
    sx={{ mr: 0.5, mb: 0.5 }}
  />
);

type Props = {
  record: Record<string, any>;
};

const LeadCompanyDashboard: React.FC<Props> = ({ record }) => {
  const [intel, setIntel] = useState<LeadIntelligence | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLeadIntelligence(Number(record.id))
      .then((data) => {
        if (active) setIntel(data);
      })
      .catch(() => {
        if (active) setIntel(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [record.id]);

  const commercial = intel?.commercial;
  const online = intel?.online_depth;
  const credco = intel?.credco;
  const gst = (intel?.gst_snapshot?.gst || intel?.gst_snapshot) as {
    legal_name?: string;
    turnover_slab?: string;
    gst_status?: string;
  } | null | undefined;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header strip */}
      <Paper sx={{ p: 2.5, mb: 2, bgcolor: 'grey.50' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" component="h1">
                {record.company_name}
              </Typography>
              {record.bucket && (
                <Chip size="small" label={record.bucket} color={bucketColors[record.bucket] || 'default'} />
              )}
              {record.is_credco_customer && (
                <Chip size="small" color="success" label={`CredCo #${record.credco_customer_id}`} />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={record.enrichment_status || 'pending'}
                color={
                  record.enrichment_status === 'complete'
                    ? 'success'
                    : record.enrichment_status === 'partial'
                      ? 'warning'
                      : 'default'
                }
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {record.master_company_id}
              {record.buyer_seller_category ? ` · ${record.buyer_seller_category}` : ''}
              {record.states ? ` · ${record.states}` : ''}
            </Typography>
            {record.recommended_action && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {record.recommended_action}
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="overline" color="text.secondary">
              Online depth
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h3" color="primary.main">
                {record.online_depth_score ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / 100
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {/* Scores */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Lead scores
            </Typography>
            <ScoreBar label="Credential" value={Number(record.credential_score || 0)} />
            <ScoreBar label="Customer" value={Number(record.customer_score || 0)} />
            <ScoreBar label="Seller" value={Number(record.seller_score || 0)} />
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Lead status: {record.lead_status || 'new'}
              {record.assigned_to ? ` · Assigned: ${record.assigned_to}` : ''}
            </Typography>
            {record.evidence_status && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Evidence: {record.evidence_status}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Commercial evidence */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Commercial evidence (compiled sales)
            </Typography>
            {commercial && commercial.buyer_observed_value + commercial.seller_observed_value > 0 ? (
              <>
                <LeadCommercialChart
                  buyerValue={commercial.buyer_observed_value}
                  sellerValue={commercial.seller_observed_value}
                  buyerRows={commercial.buyer_invoice_rows}
                  sellerRows={commercial.seller_invoice_rows}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Buyer share {formatPercent(commercial.buyer_share_pct)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seller share {formatPercent(commercial.seller_share_pct)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seller customers: {commercial.seller_customer_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Suppliers observed: {commercial.observed_supplier_count}
                  </Typography>
                </Stack>
              </>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No buyer/seller volume in master import for this company.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Market presence */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Market presence
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              {online?.signals?.map((sig) => (
                <PresenceChip
                  key={sig.key}
                  label={sig.label}
                  active={sig.active}
                  href={
                    sig.key === 'website'
                      ? record.primary_website || record.website
                      : sig.key === 'indiamart'
                        ? record.indiamart_profile_url
                        : sig.key === 'linkedin'
                          ? record.linkedin_url
                          : undefined
                  }
                />
              ))}
            </Box>
            {record.primary_website && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <LanguageIcon fontSize="small" color="action" />
                <Link href={record.primary_website} target="_blank" rel="noreferrer" variant="body2">
                  {record.primary_website}
                </Link>
                {record.has_https && <Chip size="small" label="HTTPS" variant="outlined" />}
              </Stack>
            )}
            {record.indiamart_listed && record.indiamart_product_count != null && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <StorefrontIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  IndiaMART · ~{record.indiamart_product_count} product mentions
                </Typography>
              </Stack>
            )}
            {online?.rationale_items?.length ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {online.rationale_items.join(' · ')}
              </Typography>
            ) : null}
          </Paper>
        </Grid>

        {/* Financial signals */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Financial & registry signals
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Turnover estimate
                </Typography>
                <Typography variant="h6">{formatInr(record.turnover_inr)}</Typography>
                <Chip
                  size="small"
                  label={turnoverSourceLabel(record.turnover_source)}
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
                {record.turnover_confidence != null && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Confidence {Math.round(Number(record.turnover_confidence) * 100)}%
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  GSTIN
                </Typography>
                <Typography variant="body2">{record.primary_gstin || '—'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  PAN
                </Typography>
                <Typography variant="body2">{record.pan || '—'}</Typography>
              </Grid>
              {(record.legal_name_mca || gst?.legal_name) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Legal name (registry)
                  </Typography>
                  <Typography variant="body2">{record.legal_name_mca || gst?.legal_name}</Typography>
                </Grid>
              )}
              {(gst?.turnover_slab || gst?.gst_status) && (
                <Grid item xs={12}>
                  {gst.gst_status && (
                    <Chip size="small" label={`GST: ${gst.gst_status}`} sx={{ mr: 0.5 }} />
                  )}
                  {gst.turnover_slab && (
                    <Chip size="small" label={`Slab: ${gst.turnover_slab}`} variant="outlined" />
                  )}
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* CredCo platform */}
        {record.is_credco_customer && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                CredCo platform activity
              </Typography>
              {credco?.error && (
                <Typography color="warning.main" variant="body2">
                  Could not load platform trends: {credco.error}
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <LeadCredcoTrendChart data={credco?.order_trend || []} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        12-month GMV
                      </Typography>
                      <Typography variant="h5">
                        {formatInr(credco?.totals_12m?.gmv_inr)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Orders (12m)
                      </Typography>
                      <Typography variant="h6">{credco?.totals_12m?.orders ?? 0}</Typography>
                    </Box>
                    {typeof credco?.customer?.last_login === 'string' && (
                      <Typography variant="caption" color="text.secondary">
                        Last login: {new Date(credco.customer.last_login).toLocaleDateString('en-IN')}
                      </Typography>
                    )}
                    {record.credco_relationship && (
                      <Chip size="small" label={record.credco_relationship} />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Enrichment job timeline */}
        {intel?.enrichment_jobs_summary?.latest?.length ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Enrichment jobs
              </Typography>
              <Stack spacing={0.75}>
                {intel.enrichment_jobs_summary.latest.map((job: any) => (
                  <Stack
                    key={job.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip size="small" label={job.source} variant="outlined" />
                    <Chip
                      size="small"
                      label={job.status}
                      color={
                        job.status === 'completed'
                          ? 'success'
                          : job.status === 'failed'
                            ? 'error'
                            : 'default'
                      }
                    />
                    <Typography variant="caption" color="text.secondary">
                      {job.result_summary || job.last_error || '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default LeadCompanyDashboard;
